import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';
import hasAtLeastOneFacility from '@UI/Pages/Administration/Users/Sidebars/UserSidebar/FormData/constraint/hasAtLeastOneFacility';
import deriveAuthenticationMode, {
	type CredentialType,
} from '@UI/Pages/Administration/Users/Sidebars/UserSidebar/Helpers/deriveAuthenticationMode';
import type UserView from '@UI/Store/Users/Views/UserView';

type UserFormArgs = {
	firstName: string;
	lastName: string;
	email: string;
	roleId: number | null;
	facilityIds: readonly number[];
	credentialType: CredentialType;
	isSecondFactorRequired: boolean;
};

/**
 * `email` is editable only while the edited account has never been activated; otherwise the panel
 * disables the input and `fromView` populates a valid address, so the constraint never fires.
 * The server remains the guard: it refuses an e-mail change on an activated account.
 */
@ValidatedClass()
class UserFormData extends AbstractValidatedClass<UserFormArgs> {
	@constraint(Joi.string().max(100), () => 'Le prénom ne doit pas dépasser 100 caractères.')
	@constraint(Joi.string().required(), () => 'Le prénom est requis.')
	public readonly firstName: string;

	@constraint(Joi.string().max(100), () => 'Le nom ne doit pas dépasser 100 caractères.')
	@constraint(Joi.string().required(), () => 'Le nom est requis.')
	public readonly lastName: string;

	@constraint(Joi.string().max(180), () => 'L’adresse e-mail ne doit pas dépasser 180 caractères.')
	@constraint(Joi.string().email({ tlds: false }), () => 'L’adresse e-mail est invalide.')
	@constraint(Joi.string().required(), () => 'L’adresse e-mail est requise.')
	public readonly email: string;

	@constraint(Joi.number().integer().required(), () => 'Le rôle est requis.')
	public readonly roleId: number | null;

	@constraint(hasAtLeastOneFacility, 'Sélectionnez au moins une agence.')
	public readonly facilityIds: readonly number[];

	@constraint(Joi.string().valid('password', 'sso').required(), () => 'Choisissez un mode de connexion.')
	public readonly credentialType: CredentialType;

	// A boolean has no invalid state: unlike `credentialType`, it carries no constraint.
	public readonly isSecondFactorRequired: boolean;

	constructor(data: UserFormArgs) {
		super(data);
		this.firstName = data.firstName;
		this.lastName = data.lastName;
		this.email = data.email;
		this.roleId = data.roleId;
		this.facilityIds = data.facilityIds;
		this.credentialType = data.credentialType;
		this.isSecondFactorRequired = data.isSecondFactorRequired;
		Object.freeze(this);
	}

	/**
	 * `initialFacilityIds` lets the invitation panel pre-check the current facility: the
	 * edition panel always goes through `fromView` instead, never this one.
	 */
	static createEmpty(initialFacilityIds: readonly number[] = []): UserFormData {
		return new UserFormData({
			firstName: '',
			lastName: '',
			email: '',
			roleId: null,
			facilityIds: initialFacilityIds,
			credentialType: 'password',
			// Strong side by default: what is not explicitly exempted requires the second factor.
			isSecondFactorRequired: true,
		});
	}

	static fromView(user: UserView): UserFormData {
		const { credentialType, isSecondFactorRequired } = deriveAuthenticationMode.decompose(
			user.authenticationMode.value,
		);

		return new UserFormData({
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			roleId: user.roleId,
			facilityIds: user.facilityIds,
			credentialType,
			isSecondFactorRequired,
		});
	}

	setFirstName(firstName: string): this {
		return this.cloneWith({ firstName } as Partial<UserFormArgs>);
	}

	setLastName(lastName: string): this {
		return this.cloneWith({ lastName } as Partial<UserFormArgs>);
	}

	setEmail(email: string): this {
		return this.cloneWith({ email } as Partial<UserFormArgs>);
	}

	setRoleId(roleId: number | null): this {
		return this.cloneWith({ roleId } as Partial<UserFormArgs>);
	}

	setFacilityIds(facilityIds: readonly number[]): this {
		return this.cloneWith({ facilityIds } as Partial<UserFormArgs>);
	}

	// Changing the credential type never touches `isSecondFactorRequired`: the switch's rendering
	// changes (it greys out on `sso`), but the value it carries is preserved — never derived
	// or reset here.
	setCredentialType(credentialType: CredentialType): this {
		return this.cloneWith({ credentialType } as Partial<UserFormArgs>);
	}

	setIsSecondFactorRequired(isSecondFactorRequired: boolean): this {
		return this.cloneWith({ isSecondFactorRequired } as Partial<UserFormArgs>);
	}
}

export default UserFormData;
